import type { CalendarDate } from "@internationalized/date";
import type { BestFaresPriceMaps } from "../hooks/use-best-fares-calendar";
import type { TripType } from "../hooks/use-flight-search-form";

export type SelectedToolbarPrice = {
  amount: number;
  currencyCode?: string;
  tax?: number;
  tripType: Extract<TripType, "one-way" | "round-trip">;
  usePoints: boolean;
};

type GetSelectedToolbarPriceInput = {
  isError: boolean;
  isRequestEnabled: boolean;
  prices: BestFaresPriceMaps;
  selectedDepartDate?: CalendarDate;
  selectedReturnDate?: CalendarDate;
  tripType: Extract<TripType, "one-way" | "round-trip">;
  usePoints: boolean;
};

const getPointsTax = (tax: number, shouldUsePoints: boolean) =>
  shouldUsePoints && tax > 0 ? Math.round(tax * 100) / 100 : undefined;

export const getSelectedToolbarPrice = ({
  isError,
  isRequestEnabled,
  prices,
  selectedDepartDate,
  selectedReturnDate,
  tripType,
  usePoints,
}: GetSelectedToolbarPriceInput): SelectedToolbarPrice | undefined => {
  if (!isRequestEnabled || isError || !selectedDepartDate) {
    return undefined;
  }

  const { currencyCode, inbound, outbound } = prices;
  const departFare = outbound.get(selectedDepartDate.toString());
  if (!departFare) {
    return undefined;
  }

  const { amount: departAmount, tax: departTax } = departFare;

  if (tripType === "one-way") {
    return {
      amount: departAmount,
      currencyCode,
      tax: getPointsTax(departTax, usePoints),
      tripType,
      usePoints,
    };
  }

  if (!selectedReturnDate) {
    return undefined;
  }

  const returnFare = inbound.get(selectedReturnDate.toString());
  if (!returnFare) {
    return undefined;
  }

  const { amount: returnAmount, tax: returnTax } = returnFare;

  return {
    amount: departAmount + returnAmount,
    currencyCode,
    tax: getPointsTax(departTax + returnTax, usePoints),
    tripType,
    usePoints,
  };
};
